import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import SafeMeetCancelSheet from '@/components/SafeMeetCancelSheet';
import {
  ArrowLeft, Plus, Loader2, ShieldCheck, MapPin, Clock, Calendar as CalIcon,
  User as UserIcon, CheckCircle2, AlertTriangle, ChevronDown, ChevronUp, Archive, PlusCircle,
} from 'lucide-react';

const ONE_DAY_MS = 24 * 60 * 60 * 1000;

function classify(m) {
  const isClosed = m.status === 'completed' || m.status === 'checked_in' || m.checked_in;
  const isEmergency = m.status === 'emergency' || m.status === 'sos' || m.sos_triggered;
  const isCancelled = m.status === 'cancelled';
  const dt = new Date(m.date_time).getTime();
  const stale = dt + ONE_DAY_MS < Date.now();
  const past = isClosed || isEmergency || isCancelled || stale;
  return { isClosed, isEmergency, isCancelled, stale, past };
}

function StatusPill({ meet }) {
  const { t } = useTranslation();
  const { isClosed, isEmergency, stale, isCancelled } = classify(meet);
  let bg = '#DBEAFE', fg = '#1D4ED8', Icon = CalIcon, label = t('safeMeet.statusScheduled');
  if (isEmergency)      { bg = '#FEE2E2'; fg = '#B91C1C'; Icon = AlertTriangle;  label = t('safeMeet.statusSos'); }
  else if (isCancelled) { bg = '#F3F4F6'; fg = '#6B7280'; Icon = AlertTriangle;  label = t('safeMeet.statusCancelled'); }
  else if (isClosed)    { bg = '#D1FAE5'; fg = '#047857'; Icon = CheckCircle2;   label = t('safeMeet.statusCheckedIn'); }
  else if (stale)       { bg = '#FEF3C7'; fg = '#B45309'; Icon = Clock;          label = t('safeMeet.statusAwaiting'); }
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full"
      style={{
        background: bg, color: fg,
        padding: '4px 8px', fontSize: 10, fontWeight: 900, letterSpacing: 0.5,
        textTransform: 'uppercase',
      }}
    >
      <Icon size={11} strokeWidth={2.5} />
      {label}
    </span>
  );
}

function MeetCard({ meet, onCheckin, onSOS, onCancel }) {
  const { t } = useTranslation();
  const { isClosed, isEmergency, isCancelled } = classify(meet);
  const showActions = !isClosed && !isEmergency && !isCancelled;

  return (
    <div
      className="rounded-2xl overflow-hidden"
      style={{
        background: isEmergency ? '#FEF2F2' : '#FFFFFF',
        border: `1px solid ${isEmergency ? '#FCA5A5' : '#E5E7EB'}`,
      }}
      data-testid={`meet-${meet.meet_id}`}
    >
      <div className="p-4">
        {/* Top row */}
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <MapPin size={14} style={{ color: '#B91C1C' }} strokeWidth={2.5} />
              <span className="font-bold truncate" style={{ color: '#111827', fontSize: 15 }}>
                {meet.location}
              </span>
            </div>
            <div className="flex items-center gap-1.5 mt-1">
              <Clock size={12} style={{ color: '#6B7280' }} />
              <span style={{ color: '#6B7280', fontSize: 12.5 }}>
                {new Date(meet.date_time).toLocaleString(undefined, {
                  weekday: 'short', month: 'short', day: 'numeric',
                  hour: 'numeric', minute: '2-digit',
                })}
              </span>
            </div>
          </div>
          <StatusPill meet={meet} />
        </div>

        {/* Contact box */}
        <div
          className="mt-3 pt-3 flex items-center gap-2"
          style={{ borderTop: '1px solid #E5E7EB' }}
        >
          <UserIcon size={14} style={{ color: '#6B7280' }} />
          <span style={{ color: '#111827', fontSize: 13, fontWeight: 700 }}>
            {meet.trusted_contact_name}
          </span>
          <span style={{ color: '#6B7280', fontSize: 12.5 }}>· {meet.trusted_contact_email}</span>
        </div>

        {/* Notes in curly quotes */}
        {meet.notes ? (
          <p
            className="mt-2 italic line-clamp-3"
            style={{ color: '#6B7280', fontSize: 13, lineHeight: 1.45 }}
          >
            &ldquo;{meet.notes}&rdquo;
          </p>
        ) : null}

        {/* Actions */}
        {showActions && (
          <>
            <div className="mt-3 flex items-stretch gap-2">
              <button
                type="button"
                onClick={() => onCheckin(meet)}
                data-testid={`safe-meet-checkin-${meet.meet_id}`}
                className="flex-1 rounded-xl flex items-center justify-center gap-1.5 transition-all hover:brightness-95"
                style={{
                  background: '#D1FAE5', border: '1px solid #A7F3D0',
                  color: '#047857', padding: '12px', fontSize: 13, fontWeight: 800,
                }}
              >
                <CheckCircle2 size={15} strokeWidth={2.5} />
                {t('safeMeet.checkInBtn')}
              </button>
              <button
                type="button"
                onClick={() => onSOS(meet)}
                data-testid={`safe-meet-sos-${meet.meet_id}`}
                aria-label={t('safeMeet.sosAriaLabel')}
                className="flex-1 rounded-xl flex items-center justify-center gap-1.5 transition-all hover:brightness-95"
                style={{
                  background: '#FEE2E2', border: '1px solid #FCA5A5',
                  color: '#B91C1C', padding: '12px', fontSize: 13, fontWeight: 800,
                  letterSpacing: 0.5,
                }}
              >
                <AlertTriangle size={15} strokeWidth={2.5} />
                {t('safeMeet.sosBtn')}
              </button>
            </div>

            <button
              type="button"
              onClick={() => onCancel(meet)}
              data-testid={`safe-meet-cancel-${meet.meet_id}`}
              className="mt-2.5 w-full text-center transition-colors hover:text-zinc-700"
              style={{ color: '#9CA3AF', fontSize: 12.5, fontWeight: 600 }}
            >
              {t('safeMeet.cancelBtn')}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function EmptyState({ onNew }) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-center px-1 pt-8" data-testid="safe-meet-empty">
      <div
        className="w-full rounded-[20px] flex flex-col items-center text-center"
        style={{
          background: 'linear-gradient(135deg, #10B981 0%, #047857 100%)',
          padding: '22px', gap: 10,
        }}
      >
        <ShieldCheck size={36} className="text-white" strokeWidth={2} />
        <h2 className="text-white font-black" style={{ fontSize: 20, letterSpacing: '-0.01em' }}>
          {t('safeMeet.emptyHeroTitle')}
        </h2>
        <p style={{ color: 'rgba(255,255,255,0.87)', fontSize: 13, lineHeight: 1.45 }}>
          {t('safeMeet.emptyHeroSub')}
        </p>
      </div>

      <button
        type="button"
        onClick={onNew}
        data-testid="safe-meet-empty-cta"
        className="mt-5 rounded-full text-white font-bold flex items-center gap-1.5 hover:brightness-110 transition-all"
        style={{ background: '#B91C1C', padding: '14px 20px', fontSize: 14 }}
      >
        <Plus size={16} strokeWidth={2.75} />
        {t('safeMeet.emptyCta')}
      </button>

      <p style={{ color: '#6B7280', fontSize: 12, marginTop: 12 }}>
        {t('safeMeet.emptyHint')}
      </p>
    </div>
  );
}

export default function SafeMeet() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [meets, setMeets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pastOpen, setPastOpen] = useState(false);

  const [checkinTarget, setCheckinTarget] = useState(null);
  const [sosTarget, setSosTarget] = useState(null);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [actionBusy, setActionBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get('/safe-meets');
      setMeets(r.data.meets || []);
    } catch (_e) {
      // silent
    }
  }, []);

  useEffect(() => {
    (async () => { await load(); setLoading(false); })();
  }, [load]);

  // Reload when tab regains focus (parity with mobile useFocusEffect)
  useEffect(() => {
    const onFocus = () => load();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [load]);

  const { upcoming, past } = (() => {
    const up = [], pa = [];
    for (const m of meets) {
      if (classify(m).past) pa.push(m); else up.push(m);
    }
    up.sort((a, b) => new Date(a.date_time) - new Date(b.date_time));
    pa.sort((a, b) => new Date(b.date_time) - new Date(a.date_time));
    return { upcoming: up, past: pa };
  })();

  const doCheckin = async () => {
    if (!checkinTarget) return;
    setActionBusy(true);
    try {
      await api.put(`/safe-meet/${checkinTarget.meet_id}/checkin`);
      toast.success(t('safeMeet.checkInSuccess'));
      await load();
    } catch (_e) {
      toast.error(t('safeMeet.checkInFailed'));
    } finally {
      setActionBusy(false); setCheckinTarget(null);
    }
  };

  const doSOS = async () => {
    if (!sosTarget) return;
    setActionBusy(true);
    try {
      await api.put(`/safe-meet/${sosTarget.meet_id}/sos`);
      toast.success(t('safeMeet.sosSent'));
      await load();
    } catch (_e) {
      toast.error(t('safeMeet.sosFailed'));
    } finally {
      setActionBusy(false); setSosTarget(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Loader2 className="w-7 h-7 animate-spin" style={{ color: '#B91C1C' }} />
      </div>
    );
  }

  const noMeets = meets.length === 0;
  const showNoUpcomingNudge = upcoming.length === 0 && past.length > 0;

  return (
    <div className="min-h-screen bg-white pb-[100px]" data-testid="safe-meet-page">
      {/* Header */}
      <div className="px-5 pt-6 pb-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            data-testid="safe-meet-list-back"
            aria-label={t('safeMeet.back')}
            className="w-9 h-9 rounded-full flex items-center justify-center hover:bg-zinc-100 transition-colors"
          >
            <ArrowLeft size={20} style={{ color: '#111827' }} />
          </button>
          <h1 className="font-black" style={{ color: '#111827', fontSize: 22, letterSpacing: '-0.02em' }}>
            {t('safeMeet.headerTitle')}
          </h1>
        </div>
        <button
          type="button"
          onClick={() => navigate('/safe-meet/new')}
          data-testid="safe-meet-new-btn"
          aria-label={t('safeMeet.newBtnLabel')}
          className="flex items-center justify-center rounded-full hover:brightness-110 transition-all"
          style={{ background: '#B91C1C', width: 36, height: 36 }}
        >
          <Plus size={18} className="text-white" strokeWidth={3} />
        </button>
      </div>

      {/* Body */}
      <div className="px-5" data-testid="safe-meet-list">
        {noMeets ? (
          <EmptyState onNew={() => navigate('/safe-meet/new')} />
        ) : (
          <div className="flex flex-col gap-3">
            {/* Upcoming */}
            {upcoming.length > 0 && (
              <>
                <div
                  className="uppercase font-black mt-1"
                  style={{ color: '#6B7280', fontSize: 11, letterSpacing: 1.5 }}
                >
                  {t('safeMeet.upcomingHeader', { count: upcoming.length })}
                </div>
                {upcoming.map((m) => (
                  <MeetCard
                    key={m.meet_id}
                    meet={m}
                    onCheckin={setCheckinTarget}
                    onSOS={setSosTarget}
                    onCancel={setCancelTarget}
                  />
                ))}
              </>
            )}

            {/* No-upcoming red nudge */}
            {showNoUpcomingNudge && (
              <div
                className="rounded-2xl flex items-start gap-3 mt-1"
                style={{ background: '#FEF2F2', border: '1px solid #FCA5A5', padding: 14 }}
                data-testid="safe-meet-nudge"
              >
                <PlusCircle size={20} style={{ color: '#B91C1C' }} strokeWidth={2.5} />
                <div className="flex-1">
                  <div style={{ color: '#B91C1C', fontSize: 14, fontWeight: 900 }}>
                    {t('safeMeet.noUpcomingTitle')}
                  </div>
                  <div style={{ color: '#7F1D1D', fontSize: 12, marginTop: 2 }}>
                    {t('safeMeet.noUpcomingSub')}
                  </div>
                </div>
              </div>
            )}

            {/* Past collapsible */}
            {past.length > 0 && (
              <>
                <button
                  type="button"
                  onClick={() => setPastOpen((s) => !s)}
                  data-testid="safe-meet-past-toggle"
                  className="mt-3 w-full rounded-xl flex items-center gap-2 hover:brightness-95 transition-all"
                  style={{
                    background: '#F4F4F5', border: '1px solid #E5E7EB',
                    padding: '12px 14px',
                  }}
                >
                  <Archive size={16} style={{ color: '#6B7280' }} />
                  <span className="flex-1 text-left" style={{ color: '#111827', fontSize: 13.5, fontWeight: 800 }}>
                    {t('safeMeet.pastToggle', { count: past.length })}
                  </span>
                  {pastOpen ? (
                    <ChevronUp size={16} style={{ color: '#6B7280' }} />
                  ) : (
                    <ChevronDown size={16} style={{ color: '#6B7280' }} />
                  )}
                </button>

                {pastOpen && (
                  <div className="flex flex-col gap-3 mt-1" data-testid="safe-meet-past-list">
                    {past.map((m) => (
                      <MeetCard
                        key={m.meet_id}
                        meet={m}
                        onCheckin={setCheckinTarget}
                        onSOS={setSosTarget}
                        onCancel={setCancelTarget}
                      />
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* Check-in confirm */}
      <AlertDialog open={!!checkinTarget} onOpenChange={(o) => !o && setCheckinTarget(null)}>
        <AlertDialogContent data-testid="safe-meet-checkin-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('safeMeet.checkInPromptTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('safeMeet.checkInPromptBody')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={actionBusy}>{t('safeMeet.cancelDialogCancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={doCheckin}
              disabled={actionBusy}
              data-testid="safe-meet-checkin-confirm"
              style={{ background: '#10B981' }}
            >
              {actionBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : t('safeMeet.checkInConfirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* SOS confirm */}
      <AlertDialog open={!!sosTarget} onOpenChange={(o) => !o && setSosTarget(null)}>
        <AlertDialogContent data-testid="safe-meet-sos-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('safeMeet.sosPromptTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('safeMeet.sosPromptBody')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={actionBusy}>{t('safeMeet.cancelDialogCancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={doSOS}
              disabled={actionBusy}
              data-testid="safe-meet-sos-confirm"
              style={{ background: '#B91C1C' }}
            >
              {actionBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : t('safeMeet.sosConfirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Cancel reason sheet */}
      <SafeMeetCancelSheet
        open={!!cancelTarget}
        meetId={cancelTarget?.meet_id}
        onClose={() => setCancelTarget(null)}
        onCancelled={() => { toast.success(t('safeMeet.meetCancelled')); load(); }}
      />
    </div>
  );
}
